<template>
  <div
    class="ccgd-card ccgd-card--loan"
    :class="{ selected: isSelected }"
    @click="$emit('row-click', item)"
  >
    <div class="ccgd-accent" />

    <div class="ccgd-main">
      <div class="ccgd-header ccgd-header--loan">
        <div class="ccgd-title-wrap">
          <div class="ccgd-title-row ccgd-title-row--loan">
            <div class="ccgd-title">
              {{ borrowerName }}
            </div>

            <div class="ccgd-top-right">
              <div class="ccgd-status-pill" :data-status="loanStatusLower">
                <span class="ccgd-status-dot"></span>
                {{ loanStatus }}
              </div>

              <div class="ccgd-date-pill">
                <i class="fa-duotone fa-calendar ccgd-ico ccgd-ico--date"></i>
                <span class="ccgd-date-pill-label">Est Close</span>
                <span class="ccgd-date-pill-value">{{ estCloseDateLabel }}</span>
              </div>
            </div>
          </div>

          <div class="ccgd-submeta-row">
            <div class="ccgd-meta-item-inline" v-if="loanNumber">
              <span class="ccgd-meta-label">Loan</span>
              <span class="ccgd-meta-value">
                <i class="fa-duotone fa-hashtag ccgd-ico ccgd-ico--loan"></i>
                {{ loanNumber }}
              </span>
            </div>

            <div class="ccgd-meta-item-inline" v-if="loanAmount">
              <span class="ccgd-meta-label">Amt</span>
              <span class="ccgd-meta-value">
                <i class="fa-duotone fa-dollar-sign ccgd-ico ccgd-ico--money"></i>
                {{ loanAmount }}
              </span>
            </div>

            <div class="ccgd-meta-item-inline" v-if="interestRate">
              <span class="ccgd-meta-label">Rate</span>
              <span class="ccgd-meta-value">
                <i class="fa-duotone fa-percent ccgd-ico ccgd-ico--rate"></i>
                {{ interestRate }}
              </span>
            </div>
          </div>

          <div class="ccgd-details ccgd-details--loan">
            <div class="ccgd-detail-row" v-if="lockRequestedDate">
              <i class="fa-duotone fa-calendar-plus ccgd-ico ccgd-ico--lock"></i>
              <span class="ccgd-detail-label">Lock Requested</span>
              <span class="ccgd-detail-value">{{ lockRequestedDate }}</span>
            </div>

            <div class="ccgd-detail-row" v-if="lockExpiresDate">
              <i class="fa-duotone fa-calendar-xmark ccgd-ico ccgd-ico--lockexp"></i>
              <span class="ccgd-detail-label">Lock Expires</span>
              <span class="ccgd-detail-value">{{ lockExpiresDate }}</span>
            </div>
          </div>

          <div class="ccgd-purpose-row" v-if="loanPurpose">
            <i class="fa-duotone fa-diagram-project ccgd-ico ccgd-ico--purpose"></i>
            <span class="ccgd-purpose-label">Purpose</span>
            <span class="ccgd-purpose-value">{{ loanPurpose }}</span>
          </div>
        </div>
      </div>

      <div class="ccgd-footer-row ccgd-footer-row--loan">
        <div class="ccgd-foot-left">
          <button class="ccgd-lock-ext-btn" @click.stop="requestLockExtension">
            <i class="fa-duotone fa-lock-keyhole-open"></i>
            Request Lock Extension
          </button>
        </div>
        <div class="ccgd-foot-right">
          <div class="ccgd-mini" v-if="programLabel">
            <i class="fa-duotone fa-star ccgd-ico ccgd-ico--program"></i>
            <span class="ccgd-mini-label">Program</span>
            <span class="ccgd-mini-text">{{ programLabel }}</span>
          </div>

          <div class="ccgd-mini" v-if="scoreLabel">
            <i class="fa-duotone fa-chart-bar ccgd-ico ccgd-ico--score"></i>
            <span class="ccgd-mini-label">Score</span>
            <span class="ccgd-mini-text">{{ scoreLabel }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: "LoanPipelineCard",
  props: {
    item: { type: Object, required: true },
    selectedField: { type: String, default: "selected" }
  },
  computed: {
    isSelected() {
      return !!(this.item && this.selectedField && this.item[this.selectedField]);
    },
    borrowerName() {
      return this.clean(this.item.name) || "Loan";
    },
    loanNumber() {
      return this.clean(this.item.f364) || "—";
    },
    loanAmount() {
      return this.clean(this.item.f2);
    },
    interestRate() {
      return this.clean(this.item.f3);
    },
    loanPurpose() {
      return this.clean(this.item.f1401);
    },
    loanStatus() {
      return this.clean(this.item.msstatus) || "—";
    },
    loanStatusLower() {
      return (this.loanStatus || "").toLowerCase();
    },
    estCloseDate() {
      return this.item.cust17fv ? this.formatDate(this.item.cust17fv) : null;
    },
    estCloseDateLabel() {
      return this.estCloseDate || "N/A";
    },
    lockRequestedDate() {
      return this.item.f2149 ? this.formatDate(this.item.f2149) : null;
    },
    lockExpiresDate() {
      return this.item.f2151 ? this.formatDate(this.item.f2151) : null;
    },
    programLabel() {
      return this.clean(this.item.f1012 || this.item.f1013);
    },
    scoreLabel() {
      return this.clean(this.item.fico || this.item.ficoScore || this.item.f724);
    }
  },
  methods: {
    requestLockExtension() {
      const loanId = this.item?.id || this.item?.loanid || this.item?.f364
      const url = `https://swiftapp.ai/lock-extension-request${loanId ? `?loan_id=${encodeURIComponent(loanId)}` : ""}`
      window.open(url, "_blank")
    },
    clean(v) {
      if (v == null) return null;
      const s = String(v).trim();
      if (!s) return null;
      const low = s.toLowerCase();
      if (low === "undefined" || low === "null" || s === "0") return null;
      return s;
    },
    formatDate(val) {
      if (!val) return "—";
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val);
      return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
    }
  }
};
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>

<style scoped>
.ccgd-main,
.ccgd-header--loan,
.ccgd-title-wrap {
  width: 100%;
}

.ccgd-header--loan {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ccgd-title-wrap {
  flex: 1 1 auto;
  min-width: 0;
}

.ccgd-title-row--loan {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.ccgd-title {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 950;
}

.ccgd-top-right {
  flex: 0 0 auto;
  display: inline-flex;
  flex-direction: row;
  align-items: flex-end;
  gap: 8px;
  white-space: nowrap;
}

.ccgd-submeta-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.ccgd-meta-item-inline {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.70);
}

.ccgd-meta-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-meta-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 900;
  color: rgba(15, 23, 42, 0.85);
}

.ccgd-details--loan {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ccgd-detail-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 12.5px;
  color: rgba(15, 23, 42, 0.75);
}

.ccgd-detail-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-detail-value {
  font-weight: 850;
  color: rgba(15, 23, 42, 0.85);
}

.ccgd-purpose-row {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  font-size: 12.5px;
  color: rgba(15, 23, 42, 0.78);
}

.ccgd-purpose-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-purpose-value {
  font-weight: 900;
  color: rgba(15, 23, 42, 0.88);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.ccgd-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 950;
  color: rgba(15, 23, 42, 0.85);
}

.ccgd-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.9);
}

.ccgd-footer-row--loan {
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ccgd-foot-left {
  display: inline-flex;
  align-items: center;
}

.ccgd-lock-ext-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid rgba(217, 119, 6, 0.35);
  background: rgba(255, 247, 237, 0.9);
  color: #b45309;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.ccgd-lock-ext-btn:hover {
  background: rgba(253, 230, 138, 0.5);
  border-color: rgba(217, 119, 6, 0.6);
}

.ccgd-lock-ext-btn i {
  font-size: 13px;
  --fa-primary-color: #b45309;
  --fa-secondary-color: rgba(180, 83, 9, 0.3);
  --fa-secondary-opacity: 1;
}

.ccgd-foot-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.ccgd-mini {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.68);
}

.ccgd-mini-label {
  font-size: 11px;
  font-weight: 950;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  opacity: 0.65;
}

.ccgd-mini-text {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ccgd-date-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(15, 23, 42, 0.03);
  font-size: 12px;
  font-weight: 800;
  color: rgba(15, 23, 42, 0.75);
}

.ccgd-date-pill-label {
  opacity: 0.75;
  font-weight: 900;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  font-size: 11px;
}

.ccgd-date-pill-value {
  font-weight: 950;
  color: rgba(15, 23, 42, 0.90);
}

.ccgd-ico {
  font-size: 15px;
  opacity: 0.95;
}
.ccgd-ico--loan,
.ccgd-ico--purpose,
.ccgd-ico--program,
.ccgd-ico--score,
.ccgd-ico--date,
.ccgd-ico--lock,
.ccgd-ico--lockexp {
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}


.ccgd-ico--money { color: #16a34a; }
.ccgd-ico--rate { color: #f59e0b; }

</style>